import type { ImprovementAction } from '~/gql/quality/quality'
import { useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GQL_IMPROVEMENT_ACTIONS } from '~/gql/quality/quality'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'
import { downloadCSV } from '~/utils/exportCsv/utils'
import ImprovementActionDialog from './ImprovementActionDialog'
import ImprovementActionsTable from './ImprovementActionsTable'
import { actionsCsv } from './quality-csv'
import QualityRegister from './QualityRegister'
import { useQualityMutations } from './use-quality-mutations'

type IProps = {
  formatDate: (value: unknown) => string
  timeZone?: string | null
}

/** Le journal des actions d'amélioration (#328) : les lister, en ajouter, les suivre. */
function ImprovementActionsPanel({ formatDate, timeZone }: IProps) {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_IMPROVEMENT_ACTIONS, { fetchPolicy: 'cache-and-network' })
  // `null` : aucun dialogue ; `undefined` : une nouvelle action.
  const [editing, setEditing] = useState<ImprovementAction | undefined | null>(null)
  const mutations = useQualityMutations(() => {
    setEditing(null)
    void refetch()
  })
  const actions = data?.improvementActions ?? []
  const { pageRows, pagination } = useClientPagination(actions, '')

  const exportCsv = () => {
    const { headers, rows } = actionsCsv(actions, t, formatDate)
    downloadCSV(t('quality.actions.fileName'), headers, rows)
  }

  return (
    <>
      <QualityRegister
        loading={loading && !data}
        count={actions.length}
        empty={t('quality.actions.empty')}
        newLabel={t('quality.actions.new.button')}
        onNew={() => setEditing(undefined)}
        onExport={exportCsv}
      >
        <ImprovementActionsTable actions={pageRows} formatDate={formatDate} onOpen={setEditing} />
        <TablePagination {...pagination} />
      </QualityRegister>
      {editing !== null && (
        <ImprovementActionDialog
          action={editing}
          timeZone={timeZone}
          formatDate={formatDate}
          busy={mutations.busy}
          onCancel={() => setEditing(null)}
          onSave={form => void (editing ? mutations.updateAction(editing.id, form) : mutations.createAction(form))}
        />
      )}
    </>
  )
}

export default ImprovementActionsPanel
